import React, { useEffect, useState } from 'react';
import { RouteComponentProps } from '@reach/router';
import { Asset, AssetTypes } from 'state/assets/AssetTypes';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from 'state/store';
import { RFC } from 'state/StateTypes';
import { selectFilteredSensations, selectFilteredWaveforms, selectSensations, selectWaveforms } from 'state/assets/AssetSelectors';
import { Error, ErrorCodes } from 'types';
import isAuthenticated from 'hooks/isAuthenticated';
import { deleteAsset, getAssets } from 'state/assets/AssetActions';
import ViewContainer from 'components/ViewContainer';
import AssetListHeader from 'components/AssetListHeader';
import Loading from '../components/Loading';
import ErrorMessage from '../components/ErrorMessage';
import { presentErrorBasedOnCode } from '../utils';
import ListItemAsset from '../components/ListItemAsset';
import Placeholder from '../components/Placeholder';
import Modal from '../components/Modal';
import DeleteAssetConfirmationModalContent from '../components/DeleteAssetConfirmationModalContent';

interface Props extends RouteComponentProps {
   type: AssetTypes;
}
const AssetList: RFC<Props> = ({ type }) => {
   const assets: Asset[] | null = useSelector((state: AppState) =>
      type === AssetTypes.Sensation ? selectSensations(state) : selectWaveforms(state)
   );
   const filteredAssets: Asset[] | null = useSelector((state: AppState) =>
      type === AssetTypes.Sensation ? selectFilteredSensations(state) : selectFilteredWaveforms(state)
   );
   const [contentToRender, setContentToRender] = useState<Asset[] | null>(assets ?? []);
   const [error, setError] = useState<Error | null>(null);
   const adminRoleRequired = true;
   const authenticated: boolean = isAuthenticated(adminRoleRequired);
   const [loading, setLoading] = useState<boolean>(true);
   const [modalOpen, setModalOpen] = useState<boolean>(false);
   const [assetToDelete, setAssetToDelete] = useState<Asset>();
   const [isDeleting, setIsDeleting] = useState<boolean>(false);
   const dispatch = useDispatch();

   useEffect(() => {
      //ensure page height is reset on mount
      (async () => {
         if (authenticated) {
            try {
               await dispatch(getAssets());
            } catch (error) {
               //if error results from missing token it will not have a json method
               error = error.json ? await error.json() : error;
               setError(error);
            }
            setLoading(false);
         }
      })();
   }, [authenticated]);

   useEffect(() => {
      if (filteredAssets) {
         setContentToRender(filteredAssets);
      } else {
         setContentToRender(assets);
      }
   }, [filteredAssets, assets]);

   const deleteSelectedAsset = () => {
      setIsDeleting(true);
      try {
         if (assetToDelete) {
            dispatch(deleteAsset(assetToDelete));
         }
      } catch (error) {
         console.log(error);
         setError(error);
      }
      setModalOpen(false);
      setIsDeleting(false);
   };

   const presentDeleteAssetConfirmationModal = (assetId: string) => {
      setModalOpen(true);
      setAssetToDelete(assets?.find(({ _id }) => _id === assetId));
   };

   return (
      <ViewContainer>
         <AssetListHeader title={type === AssetTypes.Sensation ? 'sensations' : 'waveforms'} />
         {loading || error ? (
            !error ? (
               <Loading />
            ) : (
               <ErrorMessage>{presentErrorBasedOnCode(error.code as ErrorCodes) || 'Something went wrong while getting sensation data'}</ErrorMessage>
            )
         ) : contentToRender && !!contentToRender.length ? (
            contentToRender.map(asset =>
               assetToDelete?._id === asset._id && isDeleting ? (
                  <Loading height="5%" />
               ) : (
                  <ListItemAsset asset={asset} key={asset._id} presentDeleteAssetConfirmationModal={presentDeleteAssetConfirmationModal} />
               )
            )
         ) : (
            <Placeholder content="sensations" />
         )}
         <Modal isOpen={modalOpen}>
            {assetToDelete && (
               <DeleteAssetConfirmationModalContent
                  assetName={assetToDelete.name}
                  assetType={assetToDelete.type}
                  onCancel={() => setModalOpen(false)}
                  onConfirm={deleteSelectedAsset}
               />
            )}
         </Modal>
      </ViewContainer>
   );
};

export default AssetList;
